<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import ArrowIcon from '@/components/shared/ArrowIcon.vue'
import MarketingHeroArt from '@/components/marketing/MarketingHeroArt.vue'
import { ABOUT_IMAGES } from '@/assets/marketing/about/images'
import { scrollToSection } from '@/utils/motion'

withDefaults(defineProps<{ variant?: 'public' | 'dashboard' }>(), { variant: 'public' })

const { t } = useI18n()

// Modified clicks (new tab, etc.) fall through to the native href behaviour.
function jumpTo(event: MouseEvent, id: string): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
    return
  }
  event.preventDefault()
  scrollToSection(id)
}
</script>

<template>
  <section
    v-if="variant === 'public'"
    class="relative bg-slate-900 overflow-hidden min-h-[calc(100svh-4rem)] flex items-center"
    aria-labelledby="about-hero-title"
  >
    <!-- Decorative while the hero is a stub SVG. Once a real photo replaces it, switch to
         :alt="t('about.images.hero_alt')" on MarketingHeroArt (the key is kept for that moment). -->
    <MarketingHeroArt v-bind="ABOUT_IMAGES.hero" />
    <div
      class="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(16,185,129,0.12)_0%,transparent_70%)] motion-safe:animate-pulse"
      aria-hidden="true"
    ></div>

    <div class="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto py-24">
      <div class="inline-flex items-center bg-emerald-500/10 text-emerald-400 text-sm font-medium px-4 py-1.5 rounded-full mb-8">
        {{ t('about.hero.badge') }}
      </div>

      <h1 id="about-hero-title" class="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight text-balance">
        {{ t('about.hero.title') }}
      </h1>

      <p class="text-lg md:text-xl text-slate-300 mt-6 max-w-2xl mx-auto leading-relaxed">
        {{ t('about.hero.subtitle') }}
      </p>

      <div class="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <a
          href="#contact"
          class="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-8 py-4 text-base transition-colors"
          @click="jumpTo($event, 'contact')"
        >
          {{ t('about.hero.cta_contact') }}
          <ArrowIcon direction="right" class="w-4 h-4" />
        </a>
        <a
          href="#story"
          class="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-slate-900 font-semibold rounded-lg px-8 py-4 text-base transition-colors"
          @click="jumpTo($event, 'story')"
        >
          {{ t('about.hero.cta_story') }}
          <ArrowIcon direction="down" class="w-4 h-4" />
        </a>
      </div>

      <div class="mt-12 flex justify-center" aria-hidden="true">
        <ArrowIcon direction="down" class="w-6 h-6 text-slate-600 motion-safe:animate-bounce" />
      </div>
    </div>
  </section>

  <section
    v-else
    class="relative overflow-hidden rounded-2xl bg-slate-800 border border-slate-700 px-8 py-12"
    aria-labelledby="about-hero-title"
  >
    <div class="relative z-10 max-w-2xl">
      <p class="text-emerald-400 font-semibold text-sm uppercase tracking-widest">{{ t('about.hero.badge') }}</p>
      <h1 id="about-hero-title" class="mt-3 text-3xl md:text-4xl font-black text-white leading-tight text-balance">
        {{ t('about.hero.title') }}
      </h1>
      <p class="mt-4 text-slate-400 leading-relaxed">{{ t('about.hero.subtitle') }}</p>
    </div>
    <img
      :src="ABOUT_IMAGES.hero.src"
      :width="ABOUT_IMAGES.hero.width"
      :height="ABOUT_IMAGES.hero.height"
      :alt="t('about.images.hero_alt')"
      class="hidden md:block absolute right-0 top-0 h-full w-1/3 object-cover opacity-70"
    />
  </section>
</template>
